/**
 * PersonalizedBookCart Custom Element
 * Handles total price calculation and cart submission for personalized book builder
 */
class PersonalizedBookCart extends HTMLElement {
  constructor() {
    super();
    this.updateTimeout = null;
  }

  connectedCallback() {
    // Currency / money formatting (accept from data attributes, fallback to theme settings)
    this.currencyIsoCode = this.getAttribute("data-currency-iso-code") || "";
    this.currencySymbol = this.getAttribute("data-currency-symbol") || "";
    this.moneyFormat =
      this.getAttribute("data-money-format") ||
      window.theme?.settings?.moneyFormat ||
      "";
    this.moneyWithCurrencyFormat =
      this.getAttribute("data-money-with-currency-format") ||
      window.theme?.settings?.moneyWithCurrencyFormat ||
      "";
    this.currencyCodeEnabled = this.parseBoolean(
      this.getAttribute("data-currency-code-enabled"),
      window.theme?.settings?.currencyCodeEnabled,
    );

    this.render();
    this.setupEventListeners();
    // Trigger initial update after a short delay to ensure all elements are ready
    setTimeout(() => {
      this.updateCart();
    }, 100);
  }

  // Use global translations object
  get strings() {
    return theme.personalizedBookStrings || {};
  }

  get productVariants() {
    return JSON.parse(this.getAttribute("data-variants") || "[]");
  }

  get dedicationPrice() {
    return parseInt(this.getAttribute("data-dedication-price") || "0");
  }

  get photoPrice() {
    return parseInt(this.getAttribute("data-photo-price") || "0");
  }

  get dedicationVariantId() {
    return this.getAttribute("data-dedication-variant-id") || "";
  }

  get photoVariantId() {
    return this.getAttribute("data-photo-variant-id") || "";
  }

  get configureBooks() {
    const containerId = this.getAttribute("data-configure-books-id");
    return containerId ? document.getElementById(containerId) : null;
  }

  render() {
    const s = this.strings;
    this.classList.add("pbk-cart-summary", "pbk-hidden");
    this.style.marginTop = "18px";

    this.innerHTML = `
      <div class="pbk-cart-left">
        <div class="pbk-cart-items-summary" id="${this.id}-items-summary">
          <!-- Will be populated by JavaScript -->
        </div>
      </div>
      <div class="pbk-cart-right">
        <div class="pbk-total-inline">
          <span class="label">${s.totalPrice || "Total price"}</span>
          <strong class="amount" id="${this.id}-total-amount">${this.formatPrice(
            0,
          )}</strong>
        </div>
        <button type="submit" name="add" class="button button--primary button--fixed" id="${this.id}-submit-btn">
          <span class="btn-fill" data-fill></span>
          <span class="btn-text">${s.addToCart || "Add to cart"}</span>
        </button>
      </div>
      <!-- Hidden container for generated items[...] inputs -->
      <div id="${this.id}-items-container" style="display: none;"></div>
    `;
  }

  setupEventListeners() {
    // Listen for updates from the parent form
    document.addEventListener("personalized-book:update", () => {
      this.updateCart();
    });
  }

  show() {
    this.classList.remove("pbk-hidden");
  }

  hide() {
    this.classList.add("pbk-hidden");
  }

  updateCart() {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    this.updateTimeout = setTimeout(() => {
      this.calculateAndDisplay();
    }, 50);
  }

  calculateAndDisplay() {
    const configureBooks =
      this.configureBooks?.querySelectorAll("configure-book") || [];

    if (configureBooks.length === 0) {
      this.hide();
      return;
    }

    this.show();

    let totalPrice = 0;
    let bookCount = 0;
    let dedicationCount = 0;
    let photoCount = 0;

    configureBooks.forEach((configureBook) => {
      // Use the configure-book custom element's API
      const config = configureBook.getConfiguration
        ? configureBook.getConfiguration()
        : null;
      const cardVariants = JSON.parse(configureBook.dataset.variants || "[]");
      if (!config || !config.variantId || cardVariants.length === 0) return;

      const variant = cardVariants.find(
        (v) => v.id.toString() === config.variantId,
      );
      const currentMainPrice = variant ? variant.price : cardVariants[0].price;

      let cardTotalPrice = currentMainPrice;
      bookCount++;

      // Add dedication price if selected
      if (config.dedication?.enabled && this.dedicationPrice > 0) {
        cardTotalPrice += this.dedicationPrice;
        dedicationCount++;
      }

      // Add photo price if selected
      if (config.finalPhoto?.enabled && this.photoPrice > 0) {
        cardTotalPrice += this.photoPrice;
        photoCount++;
      }

      totalPrice += cardTotalPrice;
    });

    // Update total amount
    const totalAmountEl = this.querySelector(`#${this.id}-total-amount`);
    if (totalAmountEl) {
      totalAmountEl.textContent = this.formatPrice(totalPrice);
    }

    // Update cart items summary
    const summaryEl = this.querySelector(`#${this.id}-items-summary`);
    if (summaryEl) {
      summaryEl.innerHTML = this.generateSummaryHTML(
        bookCount,
        dedicationCount,
        photoCount,
      );
    }

    // Update button text and state
    const submitBtn = this.querySelector(`#${this.id}-submit-btn`);
    if (submitBtn) {
      const btnText = this.strings.addToCart || "Add to cart";
      const btnTextEl = submitBtn.querySelector(".btn-text");
      if (btnTextEl) btnTextEl.textContent = btnText;
      submitBtn.disabled = bookCount === 0;
    }
  }

  generateSummaryHTML(bookCount, dedicationCount, photoCount) {
    const s = this.strings;
    const items = [];
    const bookWord = this.polishPlural(
      bookCount,
      s.bookSingular || "book",
      s.bookFew || "books",
      s.bookMany || "books",
    );
    const dedWord = this.polishPlural(
      dedicationCount,
      s.dedicationSingular || "dedication",
      s.dedicationFew || "dedications",
      s.dedicationMany || "dedications",
    );
    const photoWord = this.polishPlural(
      photoCount,
      s.finalPhotoSingular || "final photo",
      s.finalPhotoFew || "final photos",
      s.finalPhotoMany || "final photos",
    );

    if (bookCount > 0) items.push(`${bookCount} ${bookWord}`);

    const extras = [];
    if (dedicationCount > 0) extras.push(`${dedicationCount} ${dedWord}`);
    if (photoCount > 0) extras.push(`${photoCount} ${photoWord}`);

    if (extras.length > 0)
      items.push(`<span style="opacity:0.7">${extras.join(", ")}</span>`);

    return items.join(" • ") || this.strings.noItems || "No items";
  }

  generateCartItems(formData) {
    const configureBooks =
      this.configureBooks?.querySelectorAll("configure-book") || [];
    const itemsContainer = this.querySelector(`#${this.id}-items-container`);

    if (!itemsContainer) return [];

    itemsContainer.innerHTML = "";
    let idx = 0;
    const items = [];

    // Check if formData is an array (multiple characters) or single object
    const isMultipleCharacters = Array.isArray(formData);
    const characterDataArray = isMultipleCharacters ? formData : [formData];

    configureBooks.forEach((configureBook) => {
      // Use the configure-book custom element's API
      const config = configureBook.getConfiguration
        ? configureBook.getConfiguration()
        : null;
      if (!config) return;

      const themeKey = config.themeKey;
      const themeTitle = config.themeTitle;
      const groupId = config.groupId || this.uuid();
      const variantId = config.variantId;

      // Check if dedication is selected
      const hasDedication =
        config.dedication?.enabled && this.dedicationVariantId;

      // BOOK LINE - Build properties for all characters
      const storeLocale = (theme.settings && theme.settings.locale) || "pl";
      const bookLanguage = config.bookInEnglish ? "en" : storeLocale;

      const bookProperties = {
        _theme: themeKey,
        _theme_title: themeTitle,
        _theme_image: config.themeImage || "",
        _group_id: groupId,
        _language: bookLanguage,
        [this.strings.cartPropTheme || "Motyw"]: themeTitle,
      };

      const s = this.strings;
      // Customer-visible: English book chosen while store default is not English
      if (config.bookInEnglish && storeLocale !== "en") {
        const overrideText = (
          s.cartValBookEnglishOverride ||
          "English (override — store language: %{locale})"
        ).replace("%{locale}", storeLocale);
        bookProperties[s.cartPropBookLanguage || "Book language"] =
          overrideText;
      }

      // Add character data - use indexed properties for multiple characters
      const charPrefixTemplate =
        s.cartPropCharacterPrefix || "Postać %{number} - ";
      characterDataArray.forEach((charData, charIndex) => {
        const charNum = charIndex + 1;
        const prefix = isMultipleCharacters ? `_character${charNum}_` : "_";
        const displayPrefix = isMultipleCharacters
          ? charPrefixTemplate.replace("%{number}", charNum)
          : "";

        // Technical properties (backend processing)
        bookProperties[`${prefix}child_name`] = charData.name;
        bookProperties[`${prefix}child_age`] = charData.age;
        bookProperties[`${prefix}gender`] = charData.gender;
        bookProperties[`${prefix}eye_color`] = charData.eye;
        bookProperties[`${prefix}hair_length`] = charData.hair;
        bookProperties[`${prefix}hair_color`] = charData.hairColor;
        bookProperties[`${prefix}photo_url`] = charData.faceUrl || "";

        // Display properties (customer-facing)
        bookProperties[
          `${displayPrefix}${s.cartPropChildName || "Imię dziecka"}`
        ] = charData.name;
        bookProperties[`${displayPrefix}${s.cartPropAge || "Wiek"}`] =
          charData.age;
        bookProperties[`${displayPrefix}${s.cartPropGender || "Płeć"}`] =
          this.translateGender(charData.gender);
        bookProperties[
          `${displayPrefix}${s.cartPropEyeColor || "Kolor oczu"}`
        ] = this.translateEyeColor(charData.eye);
        bookProperties[
          `${displayPrefix}${s.cartPropHairLength || "Długość włosów"}`
        ] = this.translateHairLength(charData.hair);
        bookProperties[
          `${displayPrefix}${s.cartPropHairColor || "Kolor włosów"}`
        ] = this.translateHairColor(charData.hairColor);
        bookProperties[
          `${displayPrefix}${s.cartPropFacePhoto || "Zdjęcie twarzy"}`
        ] = charData.faceUrl || s.cartValNone || "Brak";
      });

      // Add dedication property only if dedication is selected
      if (hasDedication) {
        bookProperties[s.cartPropDedicationPage || "Strona z dedykacją"] =
          s.cartValYes || "Tak";
      }

      this.addItem(itemsContainer, {
        index: idx++,
        id: variantId,
        properties: bookProperties,
      });

      // DEDICATION UPSELL (if checked)
      if (config.dedication?.enabled && this.dedicationVariantId) {
        const dedicationTitle = config.dedication.title || "";
        const dedicationMessage = config.dedication.message || "";
        const dedicationFrom = config.dedication.from || "";
        const dedicationLayoutUrl = config.dedication?.layoutImageUrl || "";

        this.addItem(itemsContainer, {
          index: idx++,
          id: this.dedicationVariantId,
          properties: {
            // Technical properties
            _group_id: groupId,
            _related_theme: themeKey,
            _type: "dedication",
            _dedication_title: dedicationTitle,
            _dedication_message: dedicationMessage,
            _dedication_from: dedicationFrom,
            _dedication_layout_url: dedicationLayoutUrl,

            // Display properties
            [s.cartPropTheme || "Motyw"]: themeTitle,
            [s.cartPropDedicationTitle || "Tytuł dedykacji"]:
              dedicationTitle || s.cartValNoTitle || "Brak tytułu",
            [s.cartPropDedicationLayout || "Wybrany wzór strony z dedykacją"]:
              dedicationLayoutUrl || s.cartValNone || "Brak",
            [s.cartPropMessage || "Wiadomość"]:
              dedicationMessage || s.cartValNoMessage || "Brak wiadomości",
            [s.cartPropSignature || "Podpis"]:
              dedicationFrom || s.cartValNoSignature || "Brak podpisu",
          },
        });
      }

      // FINAL PHOTO UPSELL (if checked)
      if (config.finalPhoto?.enabled && this.photoVariantId) {
        const finalPhotoMessage = config.finalPhoto.message || "";
        const finalPhotoUrl = config.finalPhoto.url || "";

        this.addItem(itemsContainer, {
          index: idx++,
          id: this.photoVariantId,
          properties: {
            // Technical properties
            _group_id: groupId,
            _related_theme: themeKey,
            _type: "final_photo",
            _final_photo_message: finalPhotoMessage,
            _final_photo_url: finalPhotoUrl,

            // Display properties
            [s.cartPropTheme || "Motyw"]: themeTitle,
            [s.cartPropFinalPageMessage || "Wiadomość na ostatniej stronie"]:
              finalPhotoMessage || s.cartValNoMessage || "Brak wiadomości",
            [s.cartPropFinalPhoto || "Zdjęcie końcowe"]: finalPhotoUrl
              ? s.cartValUploaded || "Przesłane"
              : s.cartValNone || "Brak",
            [s.cartPropLastPageWithPhoto || "Pamiątkowe zdjęcie"]:
              s.cartValYes || "Tak",
          },
        });
      }
    });

    return items;
  }

  addItem(container, { index, id, properties }) {
    const wrap = document.createElement("div");
    wrap.className = "pbk-hidden";
    wrap.innerHTML = `
      <input type="hidden" name="items[${index}][id]" value="${id}">
      ${Object.entries(properties || {})
        .map(
          ([k, v]) =>
            `<input type="hidden" name="items[${index}][properties][${k}]" value="${(
              v || ""
            )
              .toString()
              .replace(/"/g, "&quot;")}">`,
        )
        .join("")}
    `;
    container.appendChild(wrap);
  }

  // Utility methods
  formatPrice(priceInCents) {
    const cents =
      typeof priceInCents === "number"
        ? priceInCents
        : parseInt(priceInCents || "0", 10);

    const format =
      this.currencyCodeEnabled && this.moneyWithCurrencyFormat
        ? this.moneyWithCurrencyFormat
        : this.moneyFormat;

    // Prefer theme's money formatter (matches Shopify money formats)
    if (window.theme?.Currency?.formatMoney && format) {
      return window.theme.Currency.formatMoney(cents, format);
    }

    // Fallback: Intl if we have currency code, else basic amount + symbol
    const locale =
      document.documentElement.getAttribute("lang") ||
      navigator.language ||
      "en";

    if (this.currencyIsoCode) {
      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency: this.currencyIsoCode,
        }).format(cents / 100);
      } catch (e) {
        // ignore and fallback below
      }
    }

    const amount = (cents / 100).toFixed(2);
    const symbol =
      this.currencySymbol || (theme.settings?.locale === "en" ? "£" : "zł");
    return `${amount} ${symbol}`;
  }

  parseBoolean(value, fallback = false) {
    if (value === undefined || value === null || value === "") {
      return !!fallback;
    }
    if (typeof value === "boolean") return value;
    const normalized = String(value).trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }

  pluralize(n, one, few, many) {
    if (theme.settings?.locale !== "pl") {
      return n === 1 ? one : few;
    }
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (n === 1) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
    return many;
  }

  // Keep legacy alias for backward compatibility
  polishPlural(n, one, few, many) {
    return this.pluralize(n, one, few, many);
  }

  uuid() {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  translateGender(gender) {
    const s = this.strings;
    const translations = {
      male: s.genderMale,
      female: s.genderFemale,
    };
    return translations[gender] || gender;
  }

  translateEyeColor(eye) {
    const s = this.strings;
    const translations = {
      blue: s.eyeBlue,
      green: s.eyeGreen,
      black: s.eyeBlack,
      brown: s.eyeBrown,
      gray: s.eyeGray,
    };
    return translations[eye] || eye;
  }

  translateHairLength(hair) {
    const s = this.strings;
    const translations = {
      bald: s.hairBald,
      very_short: s.hairVeryShort,
      ear_length: s.hairEar,
      shoulder: s.hairShoulder,
      chest: s.hairChest,
      waist_very_long: s.hairWaist,
    };
    return translations[hair] || hair;
  }

  translateHairColor(hairColor) {
    const s = this.strings;
    const translations = {
      blonde: s.hairColorBlonde,
      "dark-blonde": s.hairColorDarkBlonde,
      brown: s.hairColorBrown,
      black: s.hairColorBlack,
      red: s.hairColorRed,
      other: s.hairColorOther,
    };
    return translations[hairColor] || hairColor;
  }
}

customElements.define("personalized-book-cart", PersonalizedBookCart);
